import { toBodyHtml, type GuideStep } from "../loadProjectOverviewGuideSteps";
import { materialsCreateExistingUserSteps } from "../steps/materialsCreateExistingUserSteps";
import { materialsCreateNewUserSteps } from "../steps/materialsCreateNewUserSteps";
import { MATERIALS_CREATE_FLOW_KEY } from "../welcomeFlow.config";
import { replayGuideStep } from "./replayGuideStep";
import type { WelcomeDeckWithFlow } from "./WelcomeDeck";

// The empty state's own Material button (MaterialMapping.vue) — a plain Finish on demand, "Create your first element" on an auto run.
const ENDING_ANCHOR_ID = "inventory-empty-create-material-btn";

// No Carbon0 view describes an empty inventory (see materialsMapDeck.ts), so every step here is local, in toolbar reading order.
const MATERIALS_CREATE_STEPS: GuideStep[] = [
  {
    id: "",
    key: "materials-create-inventory-new",
    anchorId: "create-grouping-btn",
    title: "Create an element",
    bodyHtml: toBodyHtml(
      `Material creates one material with a quantity.
Type creates a build-up of layers you reuse across the project.
Excel imports a whole list at once.
IFC model appears once a 3D model is connected.`
    ),
    alreadySeen: false,
    highlight: true,
    holdsOpenMenu: "inventory-new",
    heldMenuWidth: 200,
  },
  {
    id: "",
    key: "materials-create-mapping-tools",
    anchorId: "mapping-tools-btn",
    title: "Mapping tools",
    bodyHtml: toBodyHtml(
      `Auto Mapping reuses mappings from your past projects.
AI Classification picks components and suggests classification codes.`
    ),
    alreadySeen: false,
    highlight: true,
    holdsOpenMenu: "mapping-tools",
    heldMenuWidth: 280,
  },
  {
    id: "",
    key: "materials-create-grouping-switch",
    anchorId: "grouping-switch",
    title: "Materials and types",
    bodyHtml: toBodyHtml("Show the inventory as single materials or as types."),
    alreadySeen: false,
    highlight: true,
  },
  {
    id: "",
    key: "materials-create-library",
    anchorId: "first-material",
    title: "The Library",
    bodyHtml: toBodyHtml(
      "The Library on the right holds the environmental data. Search it, limit it to the EPDs allowed for your country, and add materials to your elements."
    ),
    alreadySeen: false,
    highlight: true,
  },
  {
    id: "",
    key: "materials-create-library-new",
    anchorId: "create-material-construction-btn",
    title: "Create your own",
    bodyHtml: toBodyHtml(
      `Material creates a new material for the Library.
Construction creates a new construction, built from several materials, for the Library.`
    ),
    alreadySeen: false,
    highlight: true,
    holdsOpenMenu: "library-new",
    heldMenuWidth: 180,
  },
  {
    id: "",
    key: "materials-create-ending",
    anchorId: ENDING_ANCHOR_ID,
    title: "Your first element",
    bodyHtml: toBodyHtml("Start with one material, or load the sample elements to explore first."),
    alreadySeen: false,
    highlight: true,
  },
];

export const materialsCreateDeck: WelcomeDeckWithFlow = {
  id: "materialsCreate",
  flowKey: MATERIALS_CREATE_FLOW_KEY,
  intro: {
    new: materialsCreateNewUserSteps[0],
    existing: materialsCreateExistingUserSteps[0],
  },
  loadSteps: () => Promise.resolve([...MATERIALS_CREATE_STEPS, replayGuideStep("sub-sidebar-material-mapping-nav-link")]),
  ending: {
    anchorId: ENDING_ANCHOR_ID,
    // Only an auto run gets the CTA — an on-demand run must never open a write flow the user didn't ask for.
    ctaLabel: (context) => (context.onDemand ? undefined : "Create your first element"),
    runCta: async (context) => {
      if (!context.projectId) return;

      // Loaded lazily at click time, not at the top of the file, so this barrel-imported deck never drags the project store into other decks' node-environment tests.
      const [{ useProject }, { useModalStore }, { blockedFromProjectEditFor }] = await Promise.all([
        import("@/stores/project/project.store"),
        import("@/stores/modals/modals.store"),
        import("@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable"),
      ]);

      // Same guard MaterialMapping.vue's createGrouping('material') runs first, via the route-free sibling (this isn't component setup).
      if (blockedFromProjectEditFor(context.projectId)) return;
      useProject().setMaterialType("material");
      useModalStore().open("CreateMaterialModal", {}, { wrapperClass: "h-fit! max-w-4xl bg-slate-0" });
    },
  },
};
